import { createContext, useContext, useState, ReactNode, useCallback } from 'react'

interface LoginModalContextType {
    isLoginModalOpen: boolean
    // Whether a successful login from this modal invocation should redirect
    // the customer to My Account. True for the plain navbar "Login" click;
    // false when the modal was triggered mid-flow (reserving a space,
    // completing a stranded onboarding, viewing a protected page directly) so
    // the customer continues what they were doing instead of being bounced.
    redirectOnLoginSuccess: boolean
    setIsLoginModalOpen: (isOpen: boolean, options?: { redirectOnSuccess?: boolean }) => void
}

const LoginModalContext = createContext<LoginModalContextType | undefined>(undefined)

export const LoginModalProvider = ({ children }: { children: ReactNode }) => {
    const [isLoginModalOpen, setIsLoginModalOpenState] = useState(false)
    const [redirectOnLoginSuccess, setRedirectOnLoginSuccess] = useState(true)

    const setIsLoginModalOpen = useCallback((isOpen: boolean, options?: { redirectOnSuccess?: boolean }) => {
        if (isOpen) {
            setRedirectOnLoginSuccess(options?.redirectOnSuccess ?? true)
        }
        setIsLoginModalOpenState(isOpen)
    }, [])

    return (
        <LoginModalContext.Provider value={{ isLoginModalOpen, redirectOnLoginSuccess, setIsLoginModalOpen }}>
            {children}
        </LoginModalContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useLoginModal = () => {
    const context = useContext(LoginModalContext)
    if (context === undefined) {
        throw new Error('useLoginModal must be used within a LoginModalProvider')
    }
    return context
}
